import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { AiOutlineArrowLeft } from "react-icons/ai";
import { BsFillBagFill } from "react-icons/bs";
import { GrWorkshop } from "react-icons/gr";
import { backend_url, server } from "../../server";
import { getAllOrdersOfAdmin } from "../../redux/actions/order";
import Ratings from "../Products/Ratings";
import {
  getBuyerOrderBreakdown,
  bundlePostageLabelForBreakdown,
  getLineOriginalUnitPrice,
  getLinePaidUnitPrice,
} from "../../utils/orderPriceBreakdown";

// Currency formatter for GBP
const formatCurrency = (value) => {
    if (value === null || value === undefined) return "£0.00";
    return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
    }).format(Number(value));
};

const AdminOrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { adminOrders, adminOrderLoading } = useSelector((state) => state.order);
  const [sellers, setSellers] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [customerRating, setCustomerRating] = useState(0);
  const [stripeFees, setStripeFees] = useState({ enabled: true, percentage: 2.9, fixedFee: 0.3 });
  const [platformFees, setPlatformFees] = useState({ enabled: true, percentage: 5 });
  const [disputeThread, setDisputeThread] = useState(null);
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [resolveAction, setResolveAction] = useState("mediation");
  const [resolveSummary, setResolveSummary] = useState("");
  const [partialAmount, setPartialAmount] = useState("");
  const [resolving, setResolving] = useState(false);
  const [startSellerMessage, setStartSellerMessage] = useState("");
  const [startBuyerMessage, setStartBuyerMessage] = useState("");
  const [startingMediation, setStartingMediation] = useState(false);
  const [mediationParty, setMediationParty] = useState("seller");
  const [mediationMessage, setMediationMessage] = useState("");
  const [sendingMediationMessage, setSendingMediationMessage] = useState(false);

  useEffect(() => {
    dispatch(getAllOrdersOfAdmin());
  }, [dispatch]);

  // Fetch Stripe fees settings
  useEffect(() => {
    const fetchStripeFees = async () => {
      try {
        const { data } = await axios.get(`${server}/options/payment-settings`, {
          withCredentials: true,
        });
        if (data?.settings?.stripeFees) {
          setStripeFees(data.settings.stripeFees);
        }
        if (data?.settings?.platformFees) {
          setPlatformFees(data.settings.platformFees);
        }
      } catch (error) {
        console.log("Failed to fetch payment settings");
      }
    };
    fetchStripeFees();
  }, []);

  const order = useMemo(
    () => adminOrders?.find((item) => item._id === id),
    [adminOrders, id]
  );

  useEffect(() => {
    const vmStatus = order?.vaMediation?.status;
    if (!id || (vmStatus !== "open" && vmStatus !== "mediating")) {
      setDisputeThread(null);
      return;
    }
    let cancelled = false;
    setDisputeLoading(true);
    axios
      .get(`${server}/order/admin-dispute/${id}`, { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled) setDisputeThread(data);
      })
      .catch(() => {
        if (!cancelled) setDisputeThread(null);
      })
      .finally(() => {
        if (!cancelled) setDisputeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, order?.vaMediation?.status]);

  // Fetch customer rating
  useEffect(() => {
    const fetchCustomerRating = async () => {
      if (!order?.user?._id) return;
      try {
        const { data } = await axios.get(`${server}/order/customer-reviews/${order.user._id}`, {
          withCredentials: true,
        });
        if (data.success && data.averageRating) {
          setCustomerRating(data.averageRating);
        }
      } catch (error) {
        // Rating doesn't exist yet, that's fine
        console.log("No customer rating found");
      }
    };
    if (order) {
      fetchCustomerRating();
    }
  }, [order]);

  const orderItems = order?.cart || [];
  const totalQuantity = orderItems.reduce((acc, item) => acc + item.qty, 0);
  const priceBreakdown = useMemo(() => getBuyerOrderBreakdown(order), [order]);

  // Extract unique shopIds from order items
  const shopIds = useMemo(() => {
    if (!orderItems || orderItems.length === 0) return [];
    const uniqueShopIds = [...new Set(orderItems.map((item) => item.shopId).filter(Boolean))];
    return uniqueShopIds;
  }, [orderItems]);

  // Fetch seller information
  useEffect(() => {
    const fetchSellers = async () => {
      if (shopIds.length === 0) {
        setSellers([]);
        return;
      }

      try {
        setLoadingSellers(true);
        const sellerPromises = shopIds.map((shopId) =>
          axios.get(`${server}/shop/admin-seller/${shopId}`, { withCredentials: true })
        );
        const sellerResponses = await Promise.all(sellerPromises);
        const sellerData = sellerResponses.map((res) => res.data?.seller).filter(Boolean);
        setSellers(sellerData);
      } catch (error) {
        console.error("Error fetching seller information:", error);
        // Don't show error toast, just log it - seller info is not critical
      } finally {
        setLoadingSellers(false);
      }
    };

    if (order && shopIds.length > 0) {
      fetchSellers();
    }
  }, [order, shopIds]);

  const submitVaResolve = async () => {
    if (!id || !resolveSummary.trim()) {
      toast.error("Please enter a summary for both parties.");
      return;
    }
    if (resolveAction === "partial_refund") {
      const n = Number(partialAmount);
      if (!(n > 0)) {
        toast.error("Enter a valid partial refund amount.");
        return;
      }
    }
    setResolving(true);
    try {
      const { data } = await axios.post(
        `${server}/order/admin-dispute/${id}/resolve`,
        {
          action: resolveAction,
          summary: resolveSummary.trim(),
          partialRefundAmount: resolveAction === "partial_refund" ? Number(partialAmount) : undefined,
        },
        { withCredentials: true }
      );
      const buyerOk = data?.emailResults?.buyer?.sent;
      const sellerOk = data?.emailResults?.seller?.sent;
      if (buyerOk && sellerOk) {
        toast.success("Final decision saved. Buyer and seller emails sent.");
      } else if (buyerOk || sellerOk) {
        toast.warning(
          `Decision saved. Email sent to ${buyerOk ? "buyer" : "seller"} only — check the other party’s email on file / server logs.`
        );
      } else {
        toast.warning(
          "Decision saved, but emails did not send. Check SMTP settings and buyer/seller account emails in server logs (VA dispute email)."
        );
      }
      setResolveSummary("");
      setPartialAmount("");
      dispatch(getAllOrdersOfAdmin());
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to record outcome");
    } finally {
      setResolving(false);
    }
  };

  const startVaMediation = async () => {
    if (!id) return;
    if (!startSellerMessage.trim() && !startBuyerMessage.trim()) {
      toast.error("Add an opening message for the seller and/or buyer.");
      return;
    }
    setStartingMediation(true);
    try {
      const { data: startData } = await axios.post(
        `${server}/order/admin-dispute/${id}/start-mediation`,
        {
          sellerMessage: startSellerMessage.trim(),
          buyerMessage: startBuyerMessage.trim(),
        },
        { withCredentials: true }
      );
      const buyerOk = startData?.emailResults?.buyer?.sent;
      const sellerOk = startData?.emailResults?.seller?.sent;
      if (buyerOk || sellerOk) {
        toast.success("Mediation opened and email(s) sent to the parties.");
      } else {
        toast.warning(
          "Mediation opened, but emails may not have sent. Check SMTP / party email addresses in server logs."
        );
      }
      setStartSellerMessage("");
      setStartBuyerMessage("");
      dispatch(getAllOrdersOfAdmin());
      const { data } = await axios.get(`${server}/order/admin-dispute/${id}`, { withCredentials: true });
      setDisputeThread(data);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to start mediation");
    } finally {
      setStartingMediation(false);
    }
  };

  const sendVaMediationMessage = async () => {
    if (!id || !mediationMessage.trim()) {
      toast.error("Enter a message to send.");
      return;
    }
    setSendingMediationMessage(true);
    try {
      const { data: sendData } = await axios.post(
        `${server}/order/admin-dispute/${id}/mediation-message`,
        { party: mediationParty, text: mediationMessage.trim() },
        { withCredentials: true }
      );
      const er = sendData?.emailResults || {};
      const anySent = Boolean(er.buyer?.sent || er.seller?.sent);
      toast.success(anySent ? "Message sent and email notified." : "Message saved in chat.");
      if (!anySent) {
        toast.warning("In-app message saved, but email may not have sent — check server logs.");
      }
      setMediationMessage("");
      const { data } = await axios.get(`${server}/order/admin-dispute/${id}`, { withCredentials: true });
      setDisputeThread(data);
      dispatch(getAllOrdersOfAdmin());
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to send message");
    } finally {
      setSendingMediationMessage(false);
    }
  };

  if (adminOrderLoading && !order) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#38513b] border-r-transparent"></div>
          <p className="mt-4 text-slate-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-slate-900 mb-2">Order not found</h2>
          <p className="text-slate-600 mb-6">The order you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/admin-orders"
            className="inline-flex items-center gap-2 rounded-xl bg-[#38513b] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#2f4232] transition"
          >
            <AiOutlineArrowLeft size={18} />
            Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] py-6 sm:py-10">
      <div className="mx-auto max-w-5xl px-4">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to="/admin-orders"
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition"
            >
              <AiOutlineArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">
                Order Details
              </h1>
              <p className="text-sm text-slate-500">
                Order ID: <span className="font-mono">{order?.orderNumber ?? order?._id?.toString().substring(0, 8) ?? "N/A"}</span>
              </p>
            </div>
          </div>

        </header>

        <div className="space-y-6">
          {/* Order Items */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Order Items</h2>
            <div className="space-y-4">
              {orderItems.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 border-b border-slate-100 pb-4 last:border-0 last:pb-0"
                >
                  <img
                    src={
                      item.images && item.images.length > 0
                        ? item.images[0]?.url
                          ? `${backend_url}${item.images[0].url}`
                          : `${backend_url}${item.images[0]}`
                        : item.image
                          ? `${backend_url}${item.image}`
                          : "/placeholder-image.png"
                    }
                    alt={item.name || "Product"}
                    className="w-20 h-20 object-contain rounded-lg border border-slate-200"
                    onError={(e) => {
                      e.target.src = "/placeholder-image.png";
                    }}
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{item.name}</h3>
                    <p className="text-sm text-slate-600 mt-1">
                      Quantity: {item.qty} ×{" "}
                      {(() => {
                        const paid = getLinePaidUnitPrice(item);
                        const was = getLineOriginalUnitPrice(item);
                        const showWas = was > paid + 0.001;
                        return (
                          <>
                            {showWas ? (
                              <span className="mr-1 text-slate-400 line-through">
                                {formatCurrency(was)}
                              </span>
                            ) : null}
                            {formatCurrency(paid)}
                            {item?.isBundleItemFromOffer ? (
                              <span className="ml-2 text-[10px] font-semibold uppercase text-emerald-700">
                                Offer
                              </span>
                            ) : null}
                          </>
                        );
                      })()}
                    </p>
                    {item.shopId && sellers.length > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        Seller:{" "}
                        <span className="font-medium text-slate-700">
                          {sellers.find((s) => s._id === item.shopId)?.name || "Unknown"}
                        </span>
                      </p>
                    )}
                    <p className="text-sm font-semibold text-[#38513b] mt-2">
                      {formatCurrency((item.qty || 0) * getLinePaidUnitPrice(item))}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Seller Information */}
          {sellers.length > 0 && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <GrWorkshop size={20} className="text-[#38513b]" />
                <h2 className="text-lg font-semibold text-slate-900">
                  Seller Information {sellers.length > 1 && `(${sellers.length} sellers)`}
                </h2>
              </div>
              {loadingSellers ? (
                <div className="text-center py-4">
                  <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-[#38513b] border-r-transparent"></div>
                  <p className="mt-2 text-sm text-slate-600">Loading seller information...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sellers.map((seller, index) => {
                    // Calculate total for this seller's items
                    const sellerItems = orderItems.filter((item) => item.shopId === seller._id);
                    const sellerTotal = sellerItems.reduce(
                      (acc, item) => acc + (item.qty || 0) * getLinePaidUnitPrice(item),
                      0
                    );
                    const sellerQuantity = sellerItems.reduce((acc, item) => acc + (item.qty || 0), 0);

                    return (
                      <div
                        key={seller._id || index}
                        className="border border-slate-200 rounded-lg p-4 bg-slate-50"
                      >
                        <div className="flex items-start gap-3 mb-3">
                          {seller.avatar && (
                            <img
                              src={`${backend_url}${seller.avatar}`}
                              alt={seller.name || "Seller"}
                              className="w-12 h-12 rounded-full object-contain border-2 border-slate-200"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold text-slate-900 text-base mb-1">
                                  {seller.name || "Unknown Seller"}
                                </h3>
                                <p className="text-sm text-slate-600">{seller.email || "N/A"}</p>
                              </div>
                              {seller.status && (
                                <span
                                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                                    seller.status === "active"
                                      ? "bg-green-100 text-green-700"
                                      : seller.status === "disabled"
                                      ? "bg-red-100 text-red-700"
                                      : "bg-yellow-100 text-yellow-700"
                                  }`}
                                >
                                  {seller.status}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="grid gap-2 md:grid-cols-2 text-sm">
                          <div>
                            <span className="font-medium text-slate-700">Phone:</span>{" "}
                            <span className="text-slate-900">{seller.phoneNumber || "N/A"}</span>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">Items:</span>{" "}
                            <span className="text-slate-900">{sellerQuantity} item(s)</span>
                          </div>
                          {seller.address && (
                            <div className="md:col-span-2">
                              <span className="font-medium text-slate-700">Address:</span>{" "}
                              <span className="text-slate-900">
                                {seller.address}
                                {seller.postCode ? `, ${seller.postCode}` : ""}
                              </span>
                            </div>
                          )}
                          <div className="md:col-span-2">
                            <span className="font-medium text-slate-700">Order Value:</span>{" "}
                            <span className="font-semibold text-[#38513b]">
                              {formatCurrency(sellerTotal)}
                            </span>
                          </div>
                        </div>
                        {seller.description && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <p className="text-xs text-slate-600 line-clamp-2">
                              {seller.description}
                            </p>
                          </div>
                        )}
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <Link
                            to={`/admin-sellers`}
                            className="text-xs text-[#38513b] hover:underline font-medium"
                          >
                            View Seller Details →
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* Order Information */}
          <div className="grid gap-6 md:grid-cols-2">
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Customer Information</h2>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-medium text-slate-700">Name:</span>{" "}
                  <span className="text-slate-900">{order.user?.name || "N/A"}</span>
                </p>
                <p>
                  <span className="font-medium text-slate-700">Email:</span>{" "}
                  <span className="text-slate-900">{order.user?.email || "N/A"}</span>
                </p>
                <div className="flex items-center gap-2 pt-2">
                  <span className="font-medium text-slate-700">Customer Rating:</span>
                  {customerRating > 0 ? (
                    <div className="flex items-center gap-2">
                      <Ratings rating={customerRating} />
                      <span className="text-slate-900 font-medium">
                        {customerRating.toFixed(1)}/5
                      </span>
                    </div>
                  ) : (
                    <span className="text-slate-400">No ratings yet</span>
                  )}
                </div>
                {order.ukaraNumber && (
                  <p>
                    <span className="font-medium text-slate-700">UKARA Number:</span>{" "}
                    <span className="text-slate-900 font-mono">{order.ukaraNumber}</span>
                  </p>
                )}
                {order.ukaraStatus && (
                  <p>
                    <span className="font-medium text-slate-700">UKARA Status:</span>{" "}
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                        order.ukaraStatus === "verified"
                          ? "bg-emerald-100 text-emerald-700"
                          : order.ukaraStatus === "rejected"
                          ? "bg-rose-100 text-rose-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {order.ukaraStatus === "verified"
                        ? "Verified"
                        : order.ukaraStatus === "rejected"
                        ? "Rejected"
                        : "Pending"}
                    </span>
                  </p>
                )}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Shipping Address</h2>
              <div className="space-y-2 text-sm text-slate-600">
                {order.shippingAddress ? (
                  <>
                    <p>{order.shippingAddress.address1}</p>
                    {order.shippingAddress.address2 && <p>{order.shippingAddress.address2}</p>}
                    <p>
                      {order.shippingAddress.city}, {order.shippingAddress.postCode}
                    </p>
                  </>
                ) : (
                  <p className="text-slate-400">No shipping address available</p>
                )}
              </div>
            </section>
          </div>

          {/* Order Status & Payment Information */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Order Status & Payment</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm text-slate-600">Order Status</p>
                <p className="font-semibold text-slate-900 mt-1">
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                      order.status === "Delivered"
                        ? "bg-emerald-100 text-emerald-700"
                        : order.status === "Shipping"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {order.status || "Pending"}
                  </span>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Status is automatically managed based on tracking and delivery confirmation
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Payment Method</p>
                <p className="font-semibold text-slate-900 mt-1">
                  {order.paymentInfo?.type || "Stripe"}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Payment Status</p>
                <p className="font-semibold text-slate-900 mt-1">
                  {order.paymentInfo?.status || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Order Date</p>
                <p className="font-semibold text-slate-900 mt-1">
                  {order.createdAt
                    ? new Date(order.createdAt).toLocaleString()
                    : "N/A"}
                </p>
              </div>
              {order.deliveredAt && (
                <div>
                  <p className="text-sm text-slate-600">Delivered Date</p>
                  <p className="font-semibold text-slate-900 mt-1">
                    {new Date(order.deliveredAt).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Refunds / Disputes - full details for admin */}
          {order.refunds && order.refunds.length > 0 && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Refunds / Disputes</h2>
              <p className="text-sm text-slate-600 mb-4">Order status: <strong>{order.status}</strong></p>
              <div className="space-y-4">
                {order.refunds.map((refund, idx) => (
                  <div key={idx} className="rounded-lg border-2 border-slate-200 bg-slate-50/80 p-4">
                    <div className="flex justify-between items-start flex-wrap gap-2">
                      <p className="text-sm font-semibold text-slate-900">{refund.productName}</p>
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${
                        refund.status === "Refund Success" ? "bg-green-100 text-green-800" :
                        refund.status === "Refund Rejected" ? "bg-red-100 text-red-800" :
                        refund.status === "Refund Resolved" ? "bg-indigo-100 text-indigo-800" : "bg-yellow-100 text-yellow-800"
                      }`}>
                        {refund.status === "Refund Success" ? "Approved" : refund.status === "Refund Rejected" ? "Declined" : refund.status === "Refund Resolved" ? "Resolved (no refund)" : "Under review"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 mt-2">Requested at: {refund.requestedAt ? new Date(refund.requestedAt).toLocaleString() : "—"}</p>
                    <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                      <p className="text-xs text-slate-700"><strong>Refund form</strong></p>
                      {refund.reasonCategory && <p className="text-xs text-slate-700"><strong>Category:</strong> {refund.reasonCategory}</p>}
                      <p className="text-xs text-slate-700"><strong>Customer message:</strong> {refund.reason || "—"}</p>
                      {refund.images && refund.images.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-slate-700 mb-1">Evidence images:</p>
                          <div className="flex flex-wrap gap-2">
                            {refund.images.map((img, i) => (
                              <a key={i} href={`${backend_url}/${img}`} target="_blank" rel="noopener noreferrer" className="block">
                                <img src={`${backend_url}/${img}`} alt={`Evidence ${i + 1}`} className="w-16 h-16 object-contain rounded border border-slate-200" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs font-medium text-slate-700 mb-1">Refund breakdown (full amount to buyer, no cutoffs)</p>
                      <p className="text-xs text-slate-600">Product: {formatCurrency((refund.refundAmount || 0) - (refund.postageRefund || 0))} • Postage: {formatCurrency(refund.postageRefund || 0)} • <strong>Total: {formatCurrency(refund.refundAmount || 0)}</strong></p>
                    </div>
                    {refund.status === "Refund Rejected" && (refund.rejectionReason || "").trim() && (
                      <div className="mt-3 pt-3 border-t border-red-200 bg-red-50/50 rounded p-2">
                        <p className="text-xs font-medium text-red-800"><strong>Seller message when declining:</strong></p>
                        <p className="text-xs text-slate-900 whitespace-pre-wrap mt-1">{refund.rejectionReason}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {order.vaMediation?.status === "resolved" && order.vaMediation?.outcomeSummary && (
            <section className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-2">VA mediation outcome</h2>
              <p className="text-xs font-semibold uppercase text-[#38513b]">
                {order.vaMediation.outcomeType === "full_refund" && "Full refund"}
                {order.vaMediation.outcomeType === "partial_refund" && "Partial refund"}
                {order.vaMediation.outcomeType === "mediation" && "Mediation / guidance"}
                {order.vaMediation.outcomeType === "case_closed" && "Case closed"}
                {order.vaMediation.outcomeType === "return_required" && "Return required"}
              </p>
              <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{order.vaMediation.outcomeSummary}</p>
            </section>
          )}

          {(order.vaMediation?.status === "open" || order.vaMediation?.status === "mediating") && (
            <section className="rounded-2xl border-2 border-[#38513b]/40 bg-[#f6faf7] p-6 shadow-sm">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-1">VA dispute — mediation</h2>
                  <p className="text-sm text-slate-600">
                    Open private threads with the seller and buyer to request evidence. Record a final decision when
                    ready — both parties are notified and can appeal within 7 days.
                  </p>
                </div>
                <Link
                  to="/admin/va-mediation"
                  className="text-sm font-semibold text-[#38513b] hover:underline"
                >
                  All VA mediation cases
                </Link>
              </div>
              {order.vaMediation?.buyerNote ? (
                <div className="mb-4 rounded-lg border border-slate-200 bg-white p-3 text-sm">
                  <span className="font-medium text-slate-800">Buyer note:</span>{" "}
                  <span className="text-slate-700">{order.vaMediation.buyerNote}</span>
                </div>
              ) : null}

              <div className="mb-4">
                <h3 className="text-sm font-semibold text-slate-800 mb-2">Buyer–seller messages (reference only)</h3>
                {disputeLoading ? (
                  <p className="text-sm text-slate-500">Loading messages…</p>
                ) : disputeThread?.messages?.length ? (
                  <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-white p-3 text-sm">
                    {disputeThread.messages.map((m) => (
                      <div key={m._id} className="border-b border-slate-100 pb-2 last:border-0">
                        <p className="text-xs font-medium text-slate-600">
                          {m.senderLabel || "User"}{" "}
                          <span className="font-normal text-slate-400">
                            {m.createdAt ? new Date(m.createdAt).toLocaleString() : ""}
                          </span>
                        </p>
                        {m.text ? <p className="text-slate-800 whitespace-pre-wrap mt-0.5">{m.text}</p> : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">
                    No messages in the buyer–seller thread yet.
                  </p>
                )}
              </div>

              {order.vaMediation?.status === "open" && (
                <div className="mb-6 space-y-3 rounded-xl border border-slate-200 bg-white p-4">
                  <h3 className="text-sm font-semibold text-slate-800">Step 1 — Open mediation dialogue</h3>
                  <p className="text-xs text-slate-600">
                    This creates separate private VA chats with the seller and buyer. They can respond and submit
                    evidence. The buyer will not see the seller&apos;s thread.
                  </p>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Opening message to seller</label>
                    <textarea
                      value={startSellerMessage}
                      onChange={(e) => setStartSellerMessage(e.target.value)}
                      rows={3}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      placeholder="Explain what you need from the seller (evidence, tracking, etc.)."
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Opening message to buyer (optional)</label>
                    <textarea
                      value={startBuyerMessage}
                      onChange={(e) => setStartBuyerMessage(e.target.value)}
                      rows={2}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      placeholder="Only if you need information from the buyer now."
                    />
                  </div>
                  <button
                    type="button"
                    onClick={startVaMediation}
                    disabled={startingMediation}
                    className="rounded-xl bg-[#38513b] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#2f4232] disabled:opacity-50"
                  >
                    {startingMediation ? "Opening…" : "Open mediation & notify parties"}
                  </button>
                </div>
              )}

              {order.vaMediation?.status === "mediating" && (
                <div className="mb-6 space-y-4 rounded-xl border border-slate-200 bg-white p-4">
                  <h3 className="text-sm font-semibold text-slate-800">Active mediation threads</h3>
                  <div className="grid gap-4 lg:grid-cols-2">
                    <div>
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold uppercase text-slate-600">Seller thread</p>
                        {disputeThread?.sellerConversationId ? (
                          <Link
                            to={`/admin/conversations?conversation=${disputeThread.sellerConversationId}`}
                            className="text-xs font-semibold text-[#38513b] hover:underline"
                          >
                            Open in inbox
                          </Link>
                        ) : null}
                      </div>
                      <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm">
                        {(disputeThread?.sellerMediationMessages || []).length ? (
                          disputeThread.sellerMediationMessages.map((m) => (
                            <div key={m._id} className="border-b border-slate-100 pb-1 last:border-0">
                              <p className="text-xs text-slate-500">
                                {m.senderLabel}{" "}
                                {m.createdAt ? new Date(m.createdAt).toLocaleString() : ""}
                              </p>
                              {m.text ? <p className="whitespace-pre-wrap text-slate-800">{m.text}</p> : null}
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-slate-500">No messages yet.</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold uppercase text-slate-600">Buyer thread</p>
                        {disputeThread?.buyerConversationId ? (
                          <Link
                            to={`/admin/conversations?conversation=${disputeThread.buyerConversationId}`}
                            className="text-xs font-semibold text-[#38513b] hover:underline"
                          >
                            Open in inbox
                          </Link>
                        ) : null}
                      </div>
                      <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm">
                        {(disputeThread?.buyerMediationMessages || []).length ? (
                          disputeThread.buyerMediationMessages.map((m) => (
                            <div key={m._id} className="border-b border-slate-100 pb-1 last:border-0">
                              <p className="text-xs text-slate-500">
                                {m.senderLabel}{" "}
                                {m.createdAt ? new Date(m.createdAt).toLocaleString() : ""}
                              </p>
                              {m.text ? <p className="whitespace-pre-wrap text-slate-800">{m.text}</p> : null}
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-slate-500">No messages yet.</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-slate-200 pt-4 space-y-2">
                    <label className="text-xs font-medium text-slate-600">Send follow-up message</label>
                    <select
                      value={mediationParty}
                      onChange={(e) => setMediationParty(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    >
                      <option value="seller">Seller only</option>
                      <option value="buyer">Buyer only</option>
                      <option value="both">Both (same message)</option>
                    </select>
                    <textarea
                      value={mediationMessage}
                      onChange={(e) => setMediationMessage(e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      placeholder="Request evidence, clarify details, etc."
                    />
                    <button
                      type="button"
                      onClick={sendVaMediationMessage}
                      disabled={sendingMediationMessage}
                      className="rounded-xl border border-[#38513b] bg-white px-4 py-2 text-sm font-semibold text-[#38513b] hover:bg-[#f6faf7] disabled:opacity-50"
                    >
                      {sendingMediationMessage ? "Sending…" : "Send message"}
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-slate-800">
                  {order.vaMediation?.status === "mediating" ? "Step 2 — Final decision" : "Final decision (after mediation)"}
                </h3>
                <label className="block text-sm font-medium text-slate-700">Outcome</label>
                <select
                  value={resolveAction}
                  onChange={(e) => setResolveAction(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="mediation">Issue guidance (no automatic refund)</option>
                  <option value="full_refund">Enforce full refund (credit buyer wallet)</option>
                  <option value="partial_refund">Enforce partial refund (enter amount)</option>
                  <option value="return_required">Require buyer return (standard return tracking)</option>
                  <option value="case_closed">Close case (no refund)</option>
                </select>
                {resolveAction === "partial_refund" && (
                  <div>
                    <label className="text-xs font-medium text-slate-600">Amount (£)</label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={partialAmount}
                      onChange={(e) => setPartialAmount(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-slate-600">Summary (shown to buyer & seller)</label>
                  <textarea
                    value={resolveSummary}
                    onChange={(e) => setResolveSummary(e.target.value)}
                    rows={4}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Explain the final decision clearly."
                  />
                </div>
                <button
                  type="button"
                  onClick={submitVaResolve}
                  disabled={resolving}
                  className="rounded-xl bg-[#38513b] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#2f4232] disabled:opacity-50"
                >
                  {resolving ? "Saving…" : "Issue final decision & notify parties"}
                </button>
              </div>
            </section>
          )}

          {/* Order Summary */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Price Breakdown</h2>
            <div className="space-y-3">
              {(() => {
                const orderItems = order?.cart || [];
                const subtotal = priceBreakdown.itemsSubtotal;
                const round2 = (value) => Number((Number(value) || 0).toFixed(2));
                const parseMoney = (value) => {
                  if (value === null || value === undefined) return 0;
                  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
                  // Handles strings like "£12.34", "12,34", etc.
                  let s = String(value).trim();
                  // If it's "12,34" (comma decimal) -> convert to "12.34"
                  // If it includes both "." and "," -> treat "," as thousands separator and remove it.
                  const hasDot = s.includes(".");
                  const hasComma = s.includes(",");
                  if (hasComma && !hasDot) {
                    s = s.replace(",", ".");
                  } else if (hasComma && hasDot) {
                    s = s.replace(/,/g, "");
                  }
                  const cleaned = s.replace(/[^0-9.\-]/g, "");
                  const n = Number(cleaned);
                  return Number.isFinite(n) ? n : 0;
                };

                const buyerProtectionFee = round2(parseMoney(order?.buyerProtectionFee ?? 0));
                const platformFeeAmount = platformFees.enabled
                  ? round2((round2(subtotal) * (platformFees.percentage || 0)) / 100)
                  : 0;
                const featuredItemFeeFromCart = round2(
                  orderItems.reduce((acc, item) => {
                    if (!item) return acc;
                    // Snapshot on order create, or admin API enrichment from product.featuredPromotionFee
                    const v =
                      item?.featuredItemFee ??
                      item?.featuredFee ??
                      item?.featuredPromotionFee ??
                      0;
                    return acc + parseMoney(v);
                  }, 0)
                );

                const featuredItemFeeFromOrder = round2(parseMoney(order?.featuredItemFee ?? 0));
                // Prefer the explicit order field; fall back to cart-derived value.
                const featuredItemFee = featuredItemFeeFromOrder > 0 ? featuredItemFeeFromOrder : featuredItemFeeFromCart;
                // Prefer stored charged total (includes coupon + bundle rule discounts).
                const orderTotal = round2(priceBreakdown.totalCharged);
                const couponDiscount = round2(priceBreakdown.couponDiscount);
                const bundleDiscount = round2(priceBreakdown.bundleDiscount);
                const shippingDisplay = round2(priceBreakdown.shipping);

                // Stripe processes the checkout charge only — not the seller's separate pay-to-feature payment.
                const stripeProcessingBase = orderTotal;

                const stripeFeeAmount = stripeFees.enabled
                  ? round2(
                      (stripeProcessingBase * (stripeFees.percentage || 0)) / 100 +
                        (stripeFees.fixedFee || 0)
                    )
                  : 0;

                // Admin VA section: seller-paid featured promotion (stored on product / order snapshot),
                // buyer protection, platform fee. Stripe fee applies to checkout total only.
                // VA Order Earnings exclude featured item fee (shown separately after Stripe).
                const vaOrderEarnings = round2(buyerProtectionFee + platformFeeAmount - stripeFeeAmount);

                const netItemToSeller = round2(subtotal - couponDiscount - bundleDiscount);

                return (
                  <>
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>Items (bundle discounts)</span>
                      <span className="text-emerald-600 font-semibold">+ {formatCurrency(round2(subtotal))}</span>
                    </div>
                    {couponDiscount > 0 ? (
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <span>
                          Coupon / code discount
                          {priceBreakdown.discountCode
                            ? ` (${priceBreakdown.discountCode})`
                            : ""}
                        </span>
                        <span className="text-rose-600 font-semibold">
                          - {formatCurrency(couponDiscount)}
                        </span>
                      </div>
                    ) : null}
                    {bundleDiscount > 0 ? (
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <span>Bundle deal discount</span>
                        <span className="text-rose-600 font-semibold">
                          - {formatCurrency(bundleDiscount)}
                        </span>
                      </div>
                    ) : null}
                    {priceBreakdown.bundlePostageDiscount > 0 ? (
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <span>{bundlePostageLabelForBreakdown(priceBreakdown)}</span>
                        <span className="text-rose-600 font-semibold">
                          - {formatCurrency(priceBreakdown.bundlePostageDiscount)}
                        </span>
                      </div>
                    ) : null}
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>Buyer protection fee</span>
                      <span className="text-emerald-600 font-semibold">+ {formatCurrency(round2(buyerProtectionFee))}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-slate-500 pt-1">
                      <span>Shipping</span>
                      <span className="text-emerald-600 font-semibold">+ {formatCurrency(shippingDisplay)}</span>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-200 text-sm text-slate-500">
                      <span>Order total (charged)</span>
                      <span className="text-slate-700 font-semibold">= {formatCurrency(orderTotal)}</span>
                    </div>

                    <div className="border-t border-slate-200 my-3" />

                    {/* Cut offs: what is taken away before VA earnings */}
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>Net items (after order discounts, to seller)</span>
                      <span className="text-rose-600 font-semibold">
                        - {formatCurrency(netItemToSeller)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-slate-500 pt-1">
                      <span>Shipping (goes to seller)</span>
                      <span className="text-rose-600 font-semibold">
                        - {formatCurrency(shippingDisplay)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>Buyer protection fee </span>
                      <span className="text-emerald-600 font-semibold">
                        + {formatCurrency(round2(buyerProtectionFee))}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-slate-500 pt-1">
                      <span>Featured item Fee</span>
                      <span className="text-emerald-600 font-semibold">
                        + {formatCurrency(round2(featuredItemFee))}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-slate-500 pt-1">
                      <span>VA Fee (taken from seller)</span>
                      <span className="text-emerald-600 font-semibold">
                        + {formatCurrency(platformFeeAmount)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-slate-200 text-sm text-slate-500">
                      <span>VA Total</span>
                      <span className="text-slate-700 font-semibold">
                        = {formatCurrency(round2(buyerProtectionFee + featuredItemFee + platformFeeAmount))}
                      </span>
                    </div>

                    {/* Stripe deduction */}
                    <div className="flex items-center justify-between text-sm text-slate-500 pt-1">
                      <span>Stripe Fee</span>
                      <span className="text-rose-600 font-semibold">
                        - {formatCurrency(stripeFeeAmount)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm text-slate-500 pt-1">
                      <span>Featured item Fee</span>
                      <span className="text-rose-600 font-semibold">
                        - {formatCurrency(round2(featuredItemFee))}
                      </span>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-slate-200 text-sm">
                      <span className="text-slate-600 font-medium">VA Order Earnings</span>
                      <span className="text-slate-700 font-semibold">
                        = {formatCurrency(vaOrderEarnings)}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderDetails;

